import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import {
  theme,
  expandFontTheme,
  IconWarning
} from '@contrast-security-inc/design-system-foundations'
import { Text } from '@contrast-security-inc/design-system-typography'
import { ErrorBoundary } from '../../../../legacy-design-system/error-boundary/error-boundary'
import { LoadingMaskRectangle } from '../../../../legacy-design-system/loading-masks'
import { HTTPError } from '../../../../api/fetch/decorate-fetch'

/*
  Generic chart loading view with blocks as follows:
  [ title ] [ subTile ]
  [      chart        ]
  used internally within VrModuleChartLayout
*/
const VrModuleChartLoadingView = () => {
  return (
    <div
      data-testid="vr-module-chart-layout-loading-view"
      className={css`
        display: flex;
        flex-direction: column;
        height: 100%;
      `}
    >
      <div
        className={css`
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        `}
      >
        <LoadingMaskRectangle flex="1" height={29} />
        <LoadingMaskRectangle flex="1" height={29} margin="0 0 0 8px" />
      </div>
      <LoadingMaskRectangle flex="1" />
    </div>
  )
}

/*
  Generic api failed error message for Chart layouts
*/
export const VrModuleChartLayoutErrorView = () => {
  const intl = useIntl()
  const errorColor = 'red70'

  return (
    <div
      data-testid="vr-module-chart-layout-error-view"
      className={css`
        display: flex;
        flex: 1;
        align-items: center;
        justify-content: center;
        margin-top: 16px;
        background-color: ${theme.color.neutral10};
        border-radius: 8px;
      `}
    >
      <IconWarning size="sm" color={errorColor} />
      <Text
        color={errorColor}
        className={css`
          ${expandFontTheme(theme.font.body)};
          padding-left: 8px;
        `}
      >
        {intl.formatMessage({ id: 'detail_valueReporting_apiError' })}
      </Text>
    </div>
  )
}

/*
  Generic layout for vr module widget titles
*/
export const VrModuleChartLayoutHeader = ({
  // state
  isError,
  // data
  toolbar,
  title,
  subTitle
}: {
  isError?: HTTPError | null
  toolbar?: React.ReactNode
  title: string
  subTitle?: React.ReactNode
}) => {
  return (
    <>
      <div
        className={css`
          display: flex;
          margin-bottom: 24px;
        `}
      >
        {toolbar}
      </div>
      <div
        className={css`
          display: flex;
        `}
      >
        <Text
          as="strong"
          font="titleH5"
          className={css`
            margin-right: 10px;
            flex: 2;
          `}
        >
          {title}
        </Text>
        {isError ? null : (
          <div
            className={css`
              display: flex;
              justify-content: flex-end;
              flex: 2;
            `}
          >
            {subTitle}
          </div>
        )}
      </div>
    </>
  )
}

/*
  VrModuleChartLayout - Generic chart layout component, mostly used for vuln charts currently
   used with above components VrModuleChartLayoutHeader and possibly VrModuleChartLayoutPercentageSubtitle
   if we want more detailed loading views we could slot that as well
*/
export const VrModuleChartLayout = ({
  // state
  isLoading,
  isError,
  // node placeholders
  header,
  options,
  chart
}: {
  isLoading?: boolean
  isError?: HTTPError | null
  header: React.ReactNode
  options?: React.ReactNode
  chart: React.ReactNode
}) => {
  return (
    <ErrorBoundary>
      {isLoading ? (
        <VrModuleChartLoadingView />
      ) : (
        <div
          className={css`
            display: flex;
            flex-direction: column;
            height: 100%;
          `}
        >
          {header}
          {isError ? (
            <VrModuleChartLayoutErrorView />
          ) : (
            <>
              {options && (
                <div
                  className={css`
                    padding-top: 8px;
                    padding-bottom: 12px;
                  `}
                >
                  {options}
                </div>
              )}
              {chart}
            </>
          )}
        </div>
      )}
    </ErrorBoundary>
  )
}
